import { flexRender, Header, RowSelectionState, Updater } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { useInputFocus } from '../../../hooks';
import { Icon } from '../../../Icon';
import { IconButton } from '../../../IconButton';
import { Menu } from '../../../Menu';
import { Stack } from '../../../Stack';
import { useTableContext } from '../../TableContext';
import { tableStateReducer } from '../../tableStateReducer';
import { ColumnFilter, ColumnFilterType, Order, ReducerActionType, TableFilters } from '../../types';
import { ensureString } from '../../../utils';
import { TableColumnInputFilter, TableColumnMultiSelectFilter } from './components';
import { TableColumnBoolFilter } from './components/TableColumnBoolFilter';
import { HeaderButton, HeaderFilterButton, SortingDefault, SortingIcon } from './styles';

interface TableColumnHeaderActionsProps<T> {
    align?: 'start' | 'end';
    header: Header<T, unknown>;
    filterable: boolean;
    sortable: boolean;
    setRowSelection?: (updater: Updater<RowSelectionState>) => void;
}

const TableColumnHeaderActions = <T, F extends TableFilters<T>>({
    align,
    header,
    setRowSelection,
    filterable,
    sortable,
}: TableColumnHeaderActionsProps<T>) => {
    const { t } = useLocalization();
    const { registerClickAway, deregisterClickAway } = useClickAway();
    const { state, onStateChange } = useTableContext<T, F>();
    const columnId = header.column.id as keyof T;

    const [isOpen, setIsOpen] = useState(false);
    const headerCellRef = useRef(null);
    const headerActionsRef = useRef(null);

    const [textInputRef, setTextInputFocus] = useInputFocus();

    const handleClose = () => {
        setIsOpen(false);
        deregisterClickAway();
    };

    useEffect(() => {
        if (isOpen) {
            registerClickAway({
                handler: handleClose,
                roots: [() => headerActionsRef.current],
            });

            window.setTimeout(() => {
                setTextInputFocus();
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const setColumnFilter = useCallback(
        (value?: ColumnFilter) => {
            const nextState = tableStateReducer<T, F>(state, {
                type: ReducerActionType.SetColumnFilter,
                payload: {
                    columnName: columnId,
                    columnFilter: value,
                },
            });

            onStateChange(nextState);
            setRowSelection?.({});
        },
        [columnId, onStateChange, setRowSelection, state]
    );

    const setColumnSort = () => {
        const nextState = tableStateReducer<T, F>(state, {
            type: ReducerActionType.Sort,
            payload: {
                columnName: columnId,
                currentOrder: state.sort[columnId]?.order,
            },
        });

        onStateChange(nextState);
        setRowSelection?.({});
    };

    const sortingIcon = () => {
        switch (state.sort[columnId]?.order) {
            case Order.Ascending:
                return (
                    <SortingIcon
                        value={IconName.ArrowUp}
                        size="small"
                        data-test-sort={Order.Ascending}
                        alt={ensureString({ value: t(OptimusTextKey.TableSortAscending) })}
                    />
                );
            case Order.Descending:
                return (
                    <SortingIcon
                        value={IconName.ArrowDown}
                        size="small"
                        data-test-sort={Order.Descending}
                        alt={ensureString({ value: t(OptimusTextKey.TableSortDescending) })}
                    />
                );
            default:
                return <SortingDefault value={IconName.ArrowUp} size="small" data-test-sort="default" />;
        }
    };

    const { meta } = header.column.columnDef;

    return (
        <Stack spacing="50" itemAlignment="center" alignment={align}>
            <HeaderButton
                onClick={sortable ? setColumnSort : undefined}
                ref={headerCellRef}
                size="medium"
                variant="ghost"
                aria-disabled={!sortable}
                asTag={sortable ? 'button' : 'span'}
                type="button"
            >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {sortable && sortingIcon()}
            </HeaderButton>
            {filterable ? (
                <>
                    <HeaderFilterButton
                        aria-disabled={!filterable}
                        as={IconButton}
                        asIconButton
                        icon={IconName.Filter}
                        onClick={() => setIsOpen(!isOpen)}
                        ref={headerCellRef}
                        size="small"
                        variant="ghost"
                        type="button"
                    >
                        <Icon value={IconName.Filter} size="small" />
                    </HeaderFilterButton>
                    <Menu opened={isOpen} anchorElement={headerCellRef.current} ref={headerActionsRef}>
                        {filterable &&
                        (meta?.filtersType === ColumnFilterType.String ||
                            meta?.filtersType === ColumnFilterType.Number) ? (
                            <TableColumnInputFilter
                                initialValue={(state.filters?.[columnId]?.[0]?.value as string | number) || ''}
                                header={header}
                                setColumnFilter={setColumnFilter}
                                textInputRef={textInputRef}
                            />
                        ) : null}
                        {filterable &&
                        meta?.filtersType === ColumnFilterType.MultiSelect &&
                        meta?.getMultiSelectOptions &&
                        meta.getMultiSelectOptions().length ? (
                            <TableColumnMultiSelectFilter
                                header={header}
                                setColumnFilter={setColumnFilter}
                                selectedOptions={(state.filters?.[columnId]?.[0]?.value as string[]) || []}
                            />
                        ) : null}
                        {filterable && meta?.filtersType === ColumnFilterType.Bool && meta?.getBoolOptions ? (
                            <TableColumnBoolFilter
                                header={header}
                                setColumnFilter={setColumnFilter}
                                selectedValue={state.filters?.[columnId]?.[0]?.value as boolean | undefined}
                                getBoolOptions={meta.getBoolOptions}
                            />
                        ) : null}
                    </Menu>
                </>
            ) : null}
        </Stack>
    );
};

export default TableColumnHeaderActions;
